import { appStorage } from './runtime';

/**
 * GR-003 - first-touch acquisition origin.
 *
 * The OIDC redirect destroys the query string, so the origin has to be read and
 * persisted on the very first app load and only sent later, once there is an
 * authenticated session to attach it to. This module is the only place that knows
 * the storage key and the capture rules.
 *
 * First touch means first: an origin already stored on this device is never
 * replaced, whatever a later link carries. The server enforces the same rule with
 * a unique index, so a lost or cleared device record cannot rewrite history either.
 *
 * Delivery is tracked per subject, not per device. The origin belongs to the
 * device, but the attribution row belongs to a subject, so a second account
 * signing in on the same browser has to get its own send - otherwise it would
 * never be attributed at all.
 *
 * The convention itself (lowercase, `direct`/`none` when a link carries nothing)
 * lives on the server, in `UtmConvention`. What is stored here is the raw value,
 * trimmed and length-capped; the server is the authority on how it is bucketed.
 *
 * Privacy: utm_* are campaign parameters, never personal data. The only identifier
 * written here is the OIDC subject of an account already signed in on this device,
 * which the auth client keeps in the same storage; nothing is sent to the analytics
 * provider from here.
 */

export type AcquisitionOrigin = {
  readonly source: string;
  readonly medium: string;
  readonly campaign?: string;
  readonly content?: string;
};

type StoredAttribution = {
  origin: AcquisitionOrigin;
  capturedAt: number;
  /** Subjects whose attribution has already reached the server from this device. */
  sentTo: string[];
};

export const ATTRIBUTION_STORAGE_KEY = 'senti.attribution.v1';

/** Mirrors the server fallbacks so a stored record is never half empty. */
export const DIRECT_SOURCE = 'direct';
export const NO_MEDIUM = 'none';

const MAX_VALUE_LENGTH = 512;

/**
 * How many delivered subjects to remember. Shared devices are the exception, not
 * the rule, and forgetting the oldest only costs one extra request that the server
 * answers idempotently.
 */
const MAX_REMEMBERED_SUBJECTS = 5;

const readValue = (params: URLSearchParams, key: string): string | undefined => {
  const value = params.get(key);

  if (!value) {
    return undefined;
  }

  const trimmed = value.trim().slice(0, MAX_VALUE_LENGTH);

  return trimmed.length > 0 ? trimmed : undefined;
};

const parseOrigin = (search: string): AcquisitionOrigin => {
  const params = new URLSearchParams(search);

  return {
    source: readValue(params, 'utm_source') ?? DIRECT_SOURCE,
    medium: readValue(params, 'utm_medium') ?? NO_MEDIUM,
    campaign: readValue(params, 'utm_campaign'),
    content: readValue(params, 'utm_content'),
  };
};

const readStored = (): StoredAttribution | null => {
  try {
    const raw = appStorage.local.getItem(ATTRIBUTION_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    const origin = parsed?.origin;

    if (!origin || typeof origin.source !== 'string' || typeof origin.medium !== 'string') {
      return null;
    }

    return {
      origin: {
        source: origin.source,
        medium: origin.medium,
        campaign: typeof origin.campaign === 'string' ? origin.campaign : undefined,
        content: typeof origin.content === 'string' ? origin.content : undefined,
      },
      capturedAt: typeof parsed.capturedAt === 'number' ? parsed.capturedAt : 0,
      // A record written before delivery was tracked per subject has no list. It
      // reads as "delivered to nobody", which costs one idempotent request.
      sentTo: Array.isArray(parsed.sentTo)
        ? parsed.sentTo.filter((entry): entry is string => typeof entry === 'string')
        : [],
    };
  } catch {
    return null;
  }
};

const writeStored = (record: StoredAttribution): void => {
  try {
    appStorage.local.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage can be unavailable (private mode, quota). Attribution degrades to
    // best effort; it must never break the app.
  }
};

/**
 * Reads the campaign parameters of this load and stores them if this device has no
 * origin yet. Must run before anything can trigger the OIDC redirect. Returns the
 * origin that is now stored, which for a device that already had one is the old one.
 */
export const captureFirstTouch = (
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  now: number = Date.now()
): AcquisitionOrigin | null => {
  try {
    const existing = readStored();

    if (existing) {
      return existing.origin;
    }

    const origin = parseOrigin(search);

    writeStored({ origin, capturedAt: now, sentTo: [] });

    return origin;
  } catch {
    return null;
  }
};

/**
 * The origin stored on this device, whatever its delivery state. GR-004 reads it
 * to tag every funnel event with the same first touch the server stores, so the
 * weekly panel and `AcquisitionAttributions` cannot disagree about where a person
 * came from.
 */
export const storedFirstTouch = (): AcquisitionOrigin | null => readStored()?.origin ?? null;

/** The stored origin while it still has to reach the server for this subject. */
export const pendingFirstTouch = (subjectId: string): AcquisitionOrigin | null => {
  if (!subjectId) {
    return null;
  }

  const stored = readStored();

  return stored && !stored.sentTo.includes(subjectId) ? stored.origin : null;
};

const markSent = (subjectId: string): void => {
  const stored = readStored();

  if (!stored || stored.sentTo.includes(subjectId)) {
    return;
  }

  writeStored({
    ...stored,
    sentTo: [...stored.sentTo, subjectId].slice(-MAX_REMEMBERED_SUBJECTS),
  });
};

/**
 * Collapses concurrent sends for the same subject into one request. The caller sits
 * in an effect that React re-runs - twice under StrictMode, and again whenever the
 * auth object changes identity - so without this two requests leave before either
 * has recorded delivery, and the loser hits the unique index on the server.
 */
const inFlight = new Map<string, Promise<boolean>>();

/**
 * Sends the stored origin once per subject on this device. The call is idempotent
 * on the server too, so a retry after a failed send is harmless; what this avoids is
 * one request per session for ever.
 *
 * Never throws and never rejects: attribution is measurement, and measurement must
 * not be able to break an authenticated screen.
 */
export const sendFirstTouch = async (
  subjectId: string | undefined,
  send: (origin: AcquisitionOrigin) => Promise<unknown>
): Promise<boolean> => {
  if (!subjectId) {
    return false;
  }

  const running = inFlight.get(subjectId);

  if (running) {
    return running;
  }

  const origin = pendingFirstTouch(subjectId);

  if (!origin) {
    return false;
  }

  const attempt = (async (): Promise<boolean> => {
    try {
      await send(origin);
      markSent(subjectId);

      return true;
    } catch {
      // Left pending on purpose: the next authenticated load tries again.
      return false;
    }
  })();

  inFlight.set(subjectId, attempt);

  try {
    return await attempt;
  } finally {
    inFlight.delete(subjectId);
  }
};

/** Test helper. Clears the attribution record on this device. */
export const resetAttributionState = (): void => {
  inFlight.clear();

  try {
    appStorage.local.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};
